import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { SessionDraft, SessionSource } from '../models/session.model';
import { SessionMockService } from '../services/session-mock.service';
import { InvitationMockService } from '../../invitations/services/invitation-mock.service';
import { Participant, RosterRef, SplitType } from '../../invitations/models/invitation.model';
import { OrgMockService } from '../../../service/org-mock.service';

@Component({
  selector: 'app-session-wizard',
  templateUrl: './session-wizard.component.html',
  styleUrls: ['./session-wizard.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionWizardComponent implements OnInit {
  currentStep = 1;
  readonly lastStep = 3;
  invitations: { id: string; name: string; rosterName: string }[] = [];
  orgs: RosterRef[] = [];
  form!: FormGroup;
  editId: string | null = null;
  loading = true;

  constructor(
    private fb: FormBuilder,
    private svc: SessionMockService,
    private invitationSvc: InvitationMockService,
    private orgSvc: OrgMockService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      primaryInvitationId: ['', Validators.required],
      // Holds a Date (what bsDatepicker reads/writes); converted to an ISO end-of-day string on
      // save. OPTIONAL — cleared means the activity group never expires.
      expiresAt: [null as Date | null],
      sources: [[] as SessionSource[]],
      participantIds: [[] as string[], Validators.required],
      splitType: ['diverse_exact' as SplitType, Validators.required],
      groupCount: [4],
    });

    this.invitationSvc.list({ archived: false }).subscribe(res => {
      this.invitations = res.rows.map(r => ({ id: r.id, name: r.name, rosterName: r.rosterName }));
    });
    this.orgs = this.orgSvc.listSync();

    this.editId = this.route.snapshot.paramMap.get('id');
    if (!this.editId) {
      const primary = this.route.snapshot.queryParamMap.get('primary');
      if (primary) {
        this.form.patchValue({
          primaryInvitationId: primary,
          sources: [{ kind: 'invitation', id: primary } as SessionSource],
        });
      }
      this.loading = false;
      return;
    }

    this.svc.getById(this.editId).subscribe(r => {
      if (!r) { this.router.navigate(['/activity-groups']); return; }
      this.form.patchValue({
        name: r.name,
        primaryInvitationId: r.primaryInvitationId,
        expiresAt: r.expiresAt ? new Date(r.expiresAt) : null,   // ISO → Date for the calendar
        sources: [...r.sources],
        participantIds: [...r.participantIds],
        splitType: r.splitType,
        groupCount: r.groups.length,
      });
      this.loading = false;
    });
  }

  get isEdit(): boolean { return !!this.editId; }
  get pageTitle(): string { return this.isEdit ? 'Edit Activity Group' : 'New Activity Group'; }

  private detailsValid(): boolean {
    return ['name', 'primaryInvitationId'].every(c => this.form.get(c)!.valid);
  }
  private participantsValid(): boolean { return (this.form.value.participantIds as string[]).length > 0; }

  /** Why a step won't advance, rendered inline. Empty means it will. */
  blockReason(): string {
    if (this.currentStep === 1 && !this.detailsValid()) { return 'Complete the activity details to continue.'; }
    if (this.currentStep === 2 && !this.participantsValid()) { return 'Add at least one participant to continue.'; }
    return '';
  }

  next(): void { this.goToStep(this.currentStep + 1); }
  back(): void { this.currentStep = Math.max(1, this.currentStep - 1); }

  /** Jump to a step; moving forward validates each step on the way, as the Reports wizard does. */
  goToStep(target: number): void {
    target = Math.max(1, Math.min(this.lastStep, target));
    if (target > this.currentStep) {
      for (let s = this.currentStep; s < target; s++) {
        if (s === 1 && !this.detailsValid()) {
          this.currentStep = 1;
          ['name', 'primaryInvitationId'].forEach(c => this.form.get(c)!.markAsTouched());
          return;
        }
        if (s === 2 && !this.participantsValid()) { this.currentStep = 2; return; }
      }
    }
    this.currentStep = target;
  }

  cancel(): void { this.router.navigate(['/activity-groups']); }

  /** Resolved participants for Step 3's live split preview — the picker (Step 2) never mounts
   *  when jumping straight from Step 1, so the split step resolves its own preview members. */
  previewMembers(): Participant[] { return this.invitationSvc.participantsByIds(this.form.value.participantIds); }

  private buildDraft(): SessionDraft {
    const v = this.form.value;
    return {
      name: v.name, primaryInvitationId: v.primaryInvitationId,
      sources: v.sources, participantIds: v.participantIds,
      splitType: v.splitType, groupCount: v.groupCount, expiresAt: this.toExpiryIso(v.expiresAt),
    };
  }

  /**
   * bsDatepicker gives a Date at local midnight; store the END of that day so a group set to expire
   * "on the 22nd" stays active all day, not from 12:01 AM. Tolerates an ISO string too (unchanged
   * edits that never reopened the picker keep their original value). Expiry is optional: a cleared
   * field returns `undefined` — the store's single "never expires" representation, never `''`.
   */
  private toExpiryIso(d: Date | string | null): string | undefined {
    if (!d) { return undefined; }
    const day = new Date(d);
    day.setHours(23, 59, 59, 999);
    return day.toISOString();
  }

  /** Generate is the only exit, same rationale as the Reports wizard: there is no "save without generating". */
  generate(): void {
    if (!this.detailsValid()) { this.goToStep(1); return; }
    if (!this.participantsValid()) { this.goToStep(2); return; }

    // New sessions open straight onto their board (there's something to look at immediately);
    // an edit returns to the listing, matching the Reports wizard's edit -> listing pattern.
    const done = (id: string) => {
      Swal.fire({
        title: this.isEdit ? 'Activity group saved' : 'Activity group created',
        text: this.isEdit ? 'Your changes have been saved.' : 'Opening the activity group board.',
        icon: 'success', confirmButtonText: 'Got it',
      }).then(() => {
        if (this.isEdit) { this.router.navigate(['/activity-groups']); }
        else { this.router.navigate(['/activity-groups', id]); }
      });
    };

    const draft = this.buildDraft();
    // Prune participantIds down to who is ACTUALLY still in the live pool for the chosen sources,
    // right before saving — the same staleness guard as the Reports wizard. A participant can go
    // stale between selection and save (their invitation gets archived, their profile locked) and
    // vanish from the picker's pool while surviving untouched in the form value. generate() is the
    // one choke point every save path funnels through, including Step 1 -> Step 3 via the stepper,
    // which never mounts the picker and so never gets a chance to prune.
    this.svc.pool(draft.sources).subscribe(pool => {
      const liveIds = new Set(pool.map(row => row.participant.id));
      const pruned = { ...draft, participantIds: draft.participantIds.filter(id => liveIds.has(id)) };
      if (this.editId) { this.svc.update(this.editId, pruned).subscribe(s => done(s.id)); }
      else { this.svc.create(pruned).subscribe(s => done(s.id)); }
    });
  }
}
